"use client";

import {
  ChatBubbleLeftRightIcon,
  MapIcon,
  PencilSquareIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import Modal from "@/components/ui/Modal";

type ProductHelpMenuModalProps = {
  isOpen: boolean;
  onClose: () => void;
  feedbackAllowed: boolean;
  showTour: boolean;
  onSendFeedback: () => void;
  onOpenFaqs: () => void;
  onChatSupport: () => void;
  onTakeTour: () => void;
};

export default function ProductHelpMenuModal({
  isOpen,
  onClose,
  feedbackAllowed,
  showTour,
  onSendFeedback,
  onOpenFaqs,
  onChatSupport,
  onTakeTour,
}: ProductHelpMenuModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Help" maxWidth="sm">
      <div className="space-y-3">
        <p className="text-sm text-text-light">
          Tell us what you need — feedback, answers, a person, or a quick
          walkthrough.
        </p>
        <ul className="space-y-2">
          {feedbackAllowed ? (
            <li>
              <button
                type="button"
                onClick={onSendFeedback}
                className="flex w-full items-start gap-3 rounded-xl border border-border bg-surface px-3 py-3 text-left hover:border-primary/40 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <PencilSquareIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>
                  <span className="block text-sm font-medium text-foreground">
                    Send feedback
                  </span>
                  <span className="mt-0.5 block text-xs text-text-light">
                    Report a bug, share an idea, or say what you love
                  </span>
                </span>
              </button>
            </li>
          ) : null}
          <li>
            <button
              type="button"
              onClick={onOpenFaqs}
              className="flex w-full items-start gap-3 rounded-xl border border-border bg-surface px-3 py-3 text-left hover:border-primary/40 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <QuestionMarkCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <span>
                <span className="block text-sm font-medium text-foreground">
                  FAQs
                </span>
                <span className="mt-0.5 block text-xs text-text-light">
                  Short answers for common questions
                </span>
              </span>
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={onChatSupport}
              className="flex w-full items-start gap-3 rounded-xl border border-border bg-surface px-3 py-3 text-left hover:border-primary/40 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <ChatBubbleLeftRightIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <span>
                <span className="block text-sm font-medium text-foreground">
                  Chat with support
                </span>
                <span className="mt-0.5 block text-xs text-text-light">
                  Open live chat for account or listing help
                </span>
              </span>
            </button>
          </li>
          {showTour ? (
            <li>
              <button
                type="button"
                onClick={onTakeTour}
                className="flex w-full items-start gap-3 rounded-xl border border-border bg-surface px-3 py-3 text-left hover:border-primary/40 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <MapIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>
                  <span className="block text-sm font-medium text-foreground">
                    Take a quick tour
                  </span>
                  <span className="mt-0.5 block text-xs text-text-light">
                    See how Sell It Off works
                  </span>
                </span>
              </button>
            </li>
          ) : null}
        </ul>
        {!feedbackAllowed ? (
          <p className="text-xs text-text-light">
            Product feedback isn&apos;t available right now. You can still
            browse FAQs, chat with support
            {showTour ? ", or take the tour" : ""}.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
